import {oidcProviderInfo} from "../startOidcProvider";
import {userManager} from "./userManager";
import * as Oidc from "oidc-client";

class OidcUtils {

  isOidcAuthenticated = (): boolean => {
    return this.getOidcTokenValue() !== "";
  }

  getOidcTokenValue = (): string => {
    const jsonValue = this.getOidcTokenAsJson();
    if (jsonValue) {
      const token = JSON.parse(jsonValue)
      if (token?.hasOwnProperty("access_token")) {
        return token.access_token;
      }
    }
    return "";
  }

  getOidcTokenAsJson() {
    const key = "oidc.user:" + oidcProviderInfo.authorizationEndpoint + ":" + oidcProviderInfo.clientId;
    return window.sessionStorage.getItem(key);
  }

  replaceOidcUrlWithLoginUrl = () => {
    //window.history.go(-window.history.length);
    // If press back button after logging out, there could still be the state query in the url
    // so remove everything after the ? and replace with login
    window.location.href = this.getOidcBaseUrl();
    window.history.replaceState(null, "", window.location.href);
  }

  logoutOidcProvider = async () => {
    try {
      await userManager.getUser().then((user: Oidc.User) => {
        if (user != null) {
          userManager.signoutRedirect({
            id_token_hint: user?.id_token,
            post_logout_redirect_uri: this.getOidcBaseUrl() + "#/login",
            state: user?.state
          }).catch((error) => {
            throw new Error("Failed to log out:" + error);
          });
        }
        userManager.clearStaleState();
        window.sessionStorage.clear();
      });
    } catch (error) {
      throw new Error("Error occurred while logging out : " + error);
    }
  }

  // Non-keycloak roles should be at top level of JWT claim.
  // Decode base64 encoded JWT and parse as json to get roles.
  // The roles array contains the allowed roles for the OpenID user logged in.
  getNonKeycloakOidcRolesFromToken = (): string[] => {
    const jsonValue = oidcUtils.getOidcTokenAsJson();
    if (!jsonValue) return [];

    const token = JSON.parse(window.atob(jsonValue.split('.')[1]))
    return token?.roles || [];
  }

  isOidcAdminRole = (clientId:string): boolean => {
    // Check for keycloak client roles first
    const clientRoles: string[] = this.getKeycloakOidcClientRolesFromToken(clientId);
    if (this.adminRoleExists(clientRoles)) {
      return true;
    }
    // Check for keycloak realm roles after checking for client roles
    const realmRoles: string[] = this.getKeycloakOidcRolesFromToken();
    if (this.adminRoleExists(realmRoles)) {
      return true;
    }
    // check for roles from non-keycloak oidc/oauth2 provider
    const roles: string[] = this.getNonKeycloakOidcRolesFromToken();
    return (this.adminRoleExists(roles));
  }

  // Keycloak is configured to have realm roles in realm_access
  // Decode base64 encoded JWT and parse as JSON to get to realm_access.roles
  // The roles array contains the allowed roles for the OpenID user logged in.
  getKeycloakOidcRolesFromToken = (): string[] => {
    const json = this.getOidcTokenValue();
    if (!json) return [];

    const token = JSON.parse(window.atob(json.split('.')[1]));
    return token?.realm_access?.roles || [];
  }

  // Keycloak can be configured to have client roles in resource_access.
  // Decode base64 encoded JWT and parse as JSON to get to resource_access[clientId].roles.
  // The roles array contains the allowed roles for the OpenID user logged in.
  getKeycloakOidcClientRolesFromToken = (clientId:string): string[] => {
    const json = this.getOidcTokenValue();
    if (!json) return [];

    const token = JSON.parse(window.atob(json.split('.')[1]))
    const resourceAccess = token?.resource_access;

    return resourceAccess?.[clientId]?.roles || [];
  }

  getOidcBaseUrl = () => {
    const port = window.location.port ? `:${window.location.port}` : '';
    const host = `${window.location.protocol}//${window.location.hostname}`;
    // If debug in ide, the pathname should be /studio.
    // If production, the pathname should be /apollo/studio.
    return `${host}${port}${window.location.pathname}`;
  }

  getWmsOptions = (): any => {
    if (oidcProviderInfo?.enabled) {
      return {
        requestHeaders: {
          Authorization: "Bearer " + oidcUtils.getOidcTokenValue()
        },
        credentials: true
      };
    } else {
      return {};
    }
  }

  private adminRoleExists = (roles: string[]): boolean => {
    if (roles.length > 0) {
      if (roles.find((str) => str === "ADMIN" || str === "STUDIOMANAGER")) {
        return true;
      }
    }
    return false;
  }

}

export const oidcUtils = new OidcUtils();